import { useState } from "react";
import { FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface EmailFieldProps {
  form: any;
}

const EmailField = ({ form }: EmailFieldProps) => {
  const [emailError, setEmailError] = useState("");

  const validateEmail = (email: string) => {
    if (!email) {
      setEmailError("");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError("Enter a valid email address");
      return false;
    }

    const emailDomain = email.split("@")[1]?.toLowerCase();
    if (!emailDomain) {
      setEmailError("Email must include a domain");
      return false;
    }

    // Validate TLD (top-level domain) has at least 2 characters
    const tld = emailDomain.split(".").pop();
    if (!tld || tld.length < 2) {
      setEmailError("Please enter a valid email domain");
      return false;
    }

    // Block disposable email domains
    const disposableDomains = [
      "10minutemail.com", "guerrillamail.com", "tempmail.com", "throwaway.email",
      "mailinator.com", "maildrop.cc", "temp-mail.org", "yopmail.com",
      "getnada.com", "fakeinbox.com", "trashmail.com", "discard.email",
      "sharklasers.com", "grr.la", "spam4.me", "tempr.email"
    ];

    if (disposableDomains.includes(emailDomain)) {
      setEmailError("Disposable email addresses are not allowed");
      return false;
    }

    // Check for common misspellings
    const misspellings: { [key: string]: string } = {
      "gmail.co": "gmail.com",
      "gmial.com": "gmail.com",
      "gmai.com": "gmail.com",
      "yahoo.co": "yahoo.com",
      "yahooo.com": "yahoo.com",
      "yaho.com": "yahoo.com",
      "icloud.co": "icloud.com",
      "icould.com": "icloud.com",
      "iclod.com": "icloud.com",
      "outlook.co": "outlook.com",
      "outlok.com": "outlook.com",
      "hotmail.co": "hotmail.com",
      "hotmai.com": "hotmail.com",
      "aol.co": "aol.com",
      "google.co": "gmail.com",
    };

    if (misspellings[emailDomain]) {
      setEmailError(`Did you mean ${email.split("@")[0]}@${misspellings[emailDomain]}?`);
      return false;
    }

    setEmailError("");
    return true;
  };

  return (
    <FormField
      control={form.control}
      name="email"
      render={({ field, fieldState }) => (
        <FormItem>
          <div className="flex items-center gap-2">
            <FormLabel className={fieldState.error || emailError ? "text-destructive" : ""}>Email</FormLabel>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">Email for booking confirmations and event updates</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <FormControl>
            <Input 
              placeholder="you@example.com" 
              type="email" 
              {...field}
              onChange={(e) => {
                field.onChange(e);
                validateEmail(e.target.value);
              }}
              className="bg-background/50"
            />
          </FormControl>
          {emailError && (
            <p className="text-sm text-destructive mt-1">{emailError}</p>
          )}
        </FormItem>
      )}
    />
  );
};

export default EmailField;
